import {
  canonicalGlulamGrade,
  findGlulamCatalogMatch,
  findTimberCatalogMatch,
  normalizeDimensionsForMatch,
  uniqueCanonicalGrades,
  type GlulamItem,
  type TimberItem,
} from './materialsDatabaseService';

/** Offered in species pickers even when no sawn-timber or glulam catalog row exists yet. */
export const ALWAYS_SELECTABLE_PROPOSAL_TIMBER_SPECIES = [
  'Glulam',
  'Grain-Matched Glulam',
  'GG',
] as const;

/** Lines / imports often use short codes while the glulam catalog uses full names. */
export function isProposalGlulamSpecies(species: string): boolean {
  const n = species.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!n) return false;
  if (n === 'gg' || n === 'glulam' || n === 'grain-matched glulam') return true;
  if (n.includes('glulam')) return true;
  if (n.includes('glue') && n.includes('lam')) return true;
  return false;
}

function uniqueGlulamGrades(grades: string[]): string[] {
  return Array.from(new Set(grades.map((g) => canonicalGlulamGrade(g) || g).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

/** Match glulam catalog by species when possible; fall back to grade + dimensions for glulam species codes. */
export function findGlulamCatalogMatchForProposal(
  database: GlulamItem[],
  species: string,
  grade: string,
  dimensions: string,
): GlulamItem | undefined {
  const exact = findGlulamCatalogMatch(database, species, grade, dimensions);
  if (exact) return exact;
  if (!isProposalGlulamSpecies(species)) return undefined;
  const wantGrade = canonicalGlulamGrade(grade);
  const wantDim = normalizeDimensionsForMatch(dimensions);
  if (!wantGrade || !wantDim) return undefined;
  return database.find(
    (it) =>
      canonicalGlulamGrade(it.grade) === wantGrade &&
      normalizeDimensionsForMatch(it.dimensions) === wantDim,
  );
}

function glulamGradesForProposalSpecies(glulamDb: GlulamItem[], species: string, currentGrade?: string): string[] {
  const exactSpecies = glulamDb
    .filter((item) => item.species.trim().toLowerCase() === species.trim().toLowerCase())
    .map((item) => canonicalGlulamGrade(item.grade) || item.grade);
  if (exactSpecies.length > 0) {
    const grades = [...exactSpecies];
    if (currentGrade?.trim()) grades.push(canonicalGlulamGrade(currentGrade) || currentGrade);
    return uniqueGlulamGrades(grades);
  }
  if (isProposalGlulamSpecies(species)) {
    const grades = glulamDb.map((item) => canonicalGlulamGrade(item.grade) || item.grade);
    if (currentGrade?.trim()) grades.push(canonicalGlulamGrade(currentGrade) || currentGrade);
    return uniqueGlulamGrades(grades);
  }
  return [];
}

export function buildProposalTimberSpeciesOptions(
  timberDb: TimberItem[],
  glulamDb: GlulamItem[] = [],
  extraFromLines: string[] = [],
): string[] {
  const set = new Set<string>(ALWAYS_SELECTABLE_PROPOSAL_TIMBER_SPECIES);
  for (const item of timberDb) {
    const species = item.species?.trim();
    if (species) set.add(species);
  }
  for (const item of glulamDb) {
    const species = item.species?.trim();
    if (species) set.add(species);
  }
  for (const raw of extraFromLines) {
    const species = raw?.trim();
    if (species) set.add(species);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function gradesForProposalTimberSpecies(
  timberDb: TimberItem[],
  glulamDb: GlulamItem[],
  species: string,
  currentGrade?: string,
): string[] {
  const timberGrades = timberDb.filter((item) => item.species === species).map((item) => item.grade);
  const glulamGrades = glulamGradesForProposalSpecies(glulamDb, species, currentGrade);
  if (glulamGrades.length > 0 && timberGrades.length === 0) {
    return glulamGrades;
  }
  if (isProposalGlulamSpecies(species) && timberGrades.length === 0) {
    return glulamGrades;
  }
  const merged = [...timberGrades, ...glulamGrades];
  if (currentGrade?.trim()) merged.push(currentGrade);
  return uniqueCanonicalGrades(merged);
}

export function findProposalTimberPiecePrice(
  timberDb: TimberItem[],
  glulamDb: GlulamItem[],
  species: string,
  grade: string,
  dimensions: string,
  moistureRaw?: string,
): number {
  const glulam = findGlulamCatalogMatchForProposal(glulamDb, species, grade, dimensions);
  if (glulam) return glulam.piecePrice;
  const timber = findTimberCatalogMatch(timberDb, species, grade, dimensions, moistureRaw);
  return timber?.piecePrice ?? 0;
}
